import { useState } from "react";

import { useCurrentCompany } from "hooks/useCurrentCompany";
import { appName, formatDateForExcelHeader } from "utils/excelUtils";
import { downloadFileBase64 } from "utils/utils";
import { getDateTime } from "screens/Queries/components/utils";

export type ExportColumnT = {
  key: string;
  header: string;
};

type SortConfigItemT = {
  field: string;
  order: 1 | -1;
};

type UseExportToExcelConfigT = {
  filename: string;
  reportName: string;
  columns: ExportColumnT[];
  transformRow?: (row: Record<string, unknown>) => Record<string, unknown>;
};

const sortByField = (
  data: Record<string, unknown>[],
  { field, order }: SortConfigItemT,
): Record<string, unknown>[] =>
  [...data].sort((a, b) => {
    const valA = a[field];
    const valB = b[field];

    if (valA == null && valB != null) return -order;
    if (valA != null && valB == null) return order;
    if (valA == null && valB == null) return 0;

    if (typeof valA === "string" && typeof valB === "string")
      return order * valA.localeCompare(valB);

    return order * (valA < valB ? -1 : valA > valB ? 1 : 0);
  });

const processData = (rows: Record<string, unknown>[]) =>
  rows.map((row) => {
    const cellObj: Record<string, unknown> = {};
    Object.keys(row).forEach((key) => {
      cellObj[key] = {
        value: row[key],
        style: { horizontalAlignment: "center", verticalAlignment: "bottom" },
      };
    });
    return cellObj;
  });

const buildExcelTemplate = (
  rows: Record<string, unknown>[],
  columns: ExportColumnT[],
  reportName: string,
  filename: string,
  companyName: string | undefined,
) => {
  const cols = columns.map((col) => ({
    title: col.header,
    prop: col.key,
    width: col.header.length + 20,
  }));

  return {
    filename,
    templateFileName: "simple_template.xlsx",
    replace: [
      { type: 0, tag: "#simple.name", prop: "name", default: "Simple" },
      { type: 1, tag: "#simple.company", prop: "company", default: "Simple" },
      {
        type: 1,
        tag: "#printDate",
        prop: "printDate",
        default: new Date().toLocaleDateString(),
        format: ["numberFormat", "dd mmmm yyyy"],
      },
      { type: 1, tag: "#simple.appName", prop: "appName", default: "Simple" },
      { type: 1, tag: "#simple.name", prop: "name", default: "Simple" },
      { type: 2, tag: "#simple.cols", prop: "cols" },
      {
        type: 4,
        tag: "#simple.rows",
        prop: "rows",
        colprop: "cols",
        default: "N/D",
      },
    ],
    data: {
      company: companyName,
      autoFilter: true,
      gridLinesVisible: true,
      printDate: formatDateForExcelHeader(new Date()),
      appName,
      name: reportName,
      cols,
      rows: processData(rows),
    },
  };
};

export const useExportToExcel = ({
  filename,
  reportName,
  columns,
  transformRow,
}: UseExportToExcelConfigT) => {
  const companyInfo = useCurrentCompany();
  const [isExporting, setIsExporting] = useState(false);

  const exportToExcel = async ({
    data,
    sortConfig,
  }: {
    data: Record<string, unknown>[];
    sortConfig?: SortConfigItemT[];
  }) => {
    try {
      setIsExporting(true);

      const transformed = transformRow ? data.map(transformRow) : data;
      const sorted = sortConfig?.length
        ? sortByField(transformed, sortConfig[0])
        : transformed;
      const toExcel = buildExcelTemplate(
        sorted,
        columns,
        reportName,
        filename,
        companyInfo?.name,
      );

      const dateName = getDateTime();
      await downloadFileBase64(toExcel, "xlsx", `${filename}_${dateName}`);
    } finally {
      setIsExporting(false);
    }
  };

  return { exportToExcel, isExporting };
};
